import React, { useState, useEffect } from "react";
import {
  BarChart3, Wand2, FileText, Briefcase, GraduationCap,
  Zap, Folder, Award, MessageSquare, Star, ScrollText,
  SearchCheck, Target, PenTool, Network, Eye, TrendingUp,
  Compass, Settings, Loader2, Copy, Check, Sparkles,
  CheckCircle2, ArrowRight, LinkedinIcon, X,
} from "lucide-react";
import { toast } from "sonner";

// ─── Nav ────────────────────────────────────────────────────────────────────
const NAV = [
  { group: "PROFILE", items: [
    { id: "overview",        label: "Overview",         icon: BarChart3 },
    { id: "analysis",        label: "Profile Analysis", icon: SearchCheck },
    { id: "headline",        label: "Headline",         icon: Zap },
    { id: "about",           label: "About / Summary",  icon: FileText },
    { id: "experience",      label: "Experience",       icon: Briefcase },
    { id: "education",       label: "Education",        icon: GraduationCap },
    { id: "skills",          label: "Skills",           icon: Star },
    { id: "projects",        label: "Projects",         icon: Folder },
    { id: "certifications",  label: "Certifications",   icon: Award },
    { id: "recommendations", label: "Recommendations",  icon: MessageSquare },
    { id: "featured",        label: "Featured",         icon: Star },
  ]},
  { group: "CAREER", items: [
    { id: "resume",   label: "Resume Sync", icon: ScrollText },
    { id: "ats",      label: "ATS Checker", icon: SearchCheck },
    { id: "jobmatch", label: "Job Match",   icon: Target },
  ]},
  { group: "GROWTH", items: [
    { id: "content",    label: "Content Studio", icon: PenTool },
    { id: "networking", label: "Networking",      icon: Network },
    { id: "recruiter",  label: "Recruiter Mode",  icon: Eye },
    { id: "analytics",  label: "Analytics",       icon: TrendingUp },
    { id: "brand",      label: "Brand Strategy",  icon: Compass },
  ]},
  { group: "ACCOUNT", items: [
    { id: "settings", label: "Settings", icon: Settings },
  ]},
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const tkn = () => localStorage.getItem("token");

async function streamFetch(url, body, onDelta, onDone, onError) {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tkn()}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || "Request failed");
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop();
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6);
        if (data === "[DONE]") { onDone?.(); return; }
        try { onDelta?.(JSON.parse(data).delta || ""); } catch {}
      }
    }
    onDone?.();
  } catch (e) {
    onError?.(e);
  }
}

function tryParseJson(raw) {
  try {
    return JSON.parse(raw.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim());
  } catch {
    return null;
  }
}

function CopyBtn({ text, className = "" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className={`p-1.5 text-neutral-500 hover:text-white transition ${className}`}>
      {copied ? <Check className="w-3.5 h-3.5 text-[#D3FF24]" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function ScoreRing({ score, size = 80, sw = 8 }) {
  const r = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  const color = score >= 80 ? "#D3FF24" : score >= 60 ? "#FFAA24" : "#FF3B30";
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1a1a1a" strokeWidth={sw} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={sw}
        strokeDasharray={circ} strokeDashoffset={circ - (score / 100) * circ} strokeLinecap="round" />
    </svg>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-8">
      <h2 className="font-display text-3xl font-black tracking-tighter mb-1">{title}</h2>
      <p className="text-neutral-500 text-sm">{subtitle}</p>
    </div>
  );
}

function Thinking({ label = "Analyzing…" }) {
  return (
    <div className="border border-white/10 p-12 text-center">
      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#D3FF24]" />
      <p className="text-neutral-500 text-sm">{label}</p>
    </div>
  );
}

// ─── Shared streaming hook ───────────────────────────────────────────────────
function useAIStream() {
  const [raw, setRaw] = useState("");
  const [loading, setLoading] = useState(false);

  const run = (url, body, onError) => {
    setLoading(true);
    setRaw("");
    streamFetch(url, body,
      (d) => setRaw(r => r + d),
      () => setLoading(false),
      (e) => { toast.error(e.message); setLoading(false); onError?.(e); }
    );
  };

  const parsed = !loading && raw ? tryParseJson(raw) : null;
  return { raw, loading, parsed, run };
}

// ─── Overview ───────────────────────────────────────────────────────────────
function OverviewSection({ profile, setProfile, setSection }) {
  const [saving, setSaving] = useState(false);

  const score = (() => {
    let s = 0;
    if (profile.name) s += 15;
    if (profile.headline) s += 20;
    if (profile.about) s += 20;
    if ((profile.experience || []).length) s += 20;
    if ((profile.skills || []).length >= 5) s += 15;
    if ((profile.education || []).length) s += 10;
    return s;
  })();

  const wins = [
    { id: "headline",  label: "Optimize your headline",           done: (profile.headline || "").length > 30,   effort: "5 min" },
    { id: "about",     label: "Write a compelling About section", done: (profile.about || "").length > 100,     effort: "30 min" },
    { id: "skills",    label: "Add 10+ relevant skills",          done: (profile.skills || []).length >= 10,    effort: "10 min" },
    { id: "experience",label: "Quantify achievements",            done: false,                                   effort: "1 hr" },
    { id: "ats",       label: "Run ATS compatibility check",      done: false,                                   effort: "5 min" },
    { id: "jobmatch",  label: "Match profile to a job",           done: false,                                   effort: "5 min" },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/linkedin/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tkn()}` },
        body: JSON.stringify(profile),
      });
      if (!res.ok) throw new Error("Save failed");
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const label = score < 40 ? "Starter" : score < 70 ? "Intermediate" : score < 90 ? "Advanced" : "All-Star";

  return (
    <div>
      <SectionHeader title="Overview" subtitle="Your LinkedIn optimization dashboard" />

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-white/10 p-5 flex items-center gap-4">
          <div className="relative shrink-0">
            <ScoreRing score={score} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-black">{score}</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Profile Score</div>
            <div className="text-xl font-black mt-0.5">{label}</div>
          </div>
        </div>
        <div className="border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Sections Complete</div>
          <div className="text-3xl font-black">
            {[profile.headline, profile.about, (profile.experience || []).length, (profile.skills || []).length, (profile.education || []).length].filter(Boolean).length}
            <span className="text-neutral-600 text-xl">/5</span>
          </div>
        </div>
        <div className="border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Quick Wins Left</div>
          <div className="text-3xl font-black text-[#D3FF24]">{wins.filter(w => !w.done).length}</div>
        </div>
      </div>

      <div className="border border-white/10 mb-6">
        <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Quick Wins</div>
        <div className="divide-y divide-white/5">
          {wins.map(w => (
            <div key={w.id} className="flex items-center gap-4 px-5 py-3.5">
              {w.done
                ? <CheckCircle2 className="w-4 h-4 text-[#D3FF24] shrink-0" strokeWidth={1.75} />
                : <div className="w-4 h-4 border border-white/20 rounded-full shrink-0" />}
              <span className={`flex-1 text-sm ${w.done ? "line-through text-neutral-600" : "text-neutral-300"}`}>{w.label}</span>
              <span className="text-xs text-neutral-600">{w.effort}</span>
              {!w.done && (
                <button onClick={() => setSection(w.id)}
                  className="text-xs text-[#D3FF24] border border-[#D3FF24]/30 px-2.5 py-1 hover:bg-[#D3FF24]/10 transition">
                  Fix →
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="border border-white/10">
        <div className="px-5 py-3 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Profile Data</div>
          <div className="text-[10px] text-neutral-600 mt-0.5">Used by all AI optimizers below</div>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[["name","Full Name","John Smith"],["location","Location","San Francisco, CA"],["industry","Industry","Technology"],["target_role","Target Role","Senior Software Engineer"]].map(([k, lab, ph]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500">{lab}</label>
                <input value={profile[k] || ""} onChange={e => setProfile(p => ({ ...p, [k]: e.target.value }))}
                  className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
                  placeholder={ph} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Current Headline</label>
            <input value={profile.headline || ""} onChange={e => setProfile(p => ({ ...p, headline: e.target.value }))}
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
              placeholder="Software Engineer at Acme Corp" />
          </div>
          <button onClick={save} disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-white text-black py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-200 transition disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Profile Data
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Profile Analysis ────────────────────────────────────────────────────────
function AnalysisSection() {
  const [text, setText] = useState("");
  const { raw, loading, parsed, run } = useAIStream();

  const analyze = () => {
    if (text.trim().length < 100) { toast.error("Paste more profile text (at least 100 chars)"); return; }
    run("/api/linkedin/analyze", { profile_text: text });
  };

  return (
    <div>
      <SectionHeader title="Profile Analysis" subtitle="AI scores your LinkedIn profile across all sections and surfaces improvements" />

      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Paste Your LinkedIn Profile Text</label>
          <p className="text-xs text-neutral-600 mt-0.5 mb-2">Go to linkedin.com → Select All (Ctrl+A) → Copy → Paste here</p>
          <textarea rows={8} value={text} onChange={e => setText(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Paste your full LinkedIn profile text…" />
        </div>
        <button onClick={analyze} disabled={loading || text.trim().length < 100}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><Sparkles className="w-4 h-4" /> Analyze Profile</>}
        </button>
      </div>

      {loading && !parsed && <Thinking label="Scoring your profile…" />}

      {parsed && (
        <div className="space-y-4">
          <div className="border border-white/10 p-6 flex items-center gap-8">
            <div className="relative shrink-0">
              <ScoreRing score={parsed.overall_score} size={100} sw={10} />
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className="text-2xl font-black">{parsed.overall_score}</span>
                <span className="text-[9px] text-neutral-500 uppercase">/100</span>
              </div>
            </div>
            <div>
              <div className="text-2xl font-black">{parsed.grade} · {parsed.profile_type}</div>
              <div className="flex flex-wrap gap-3 mt-2">
                {Object.entries(parsed.section_scores || {}).map(([k, v]) => (
                  <div key={k} className="text-xs flex items-center gap-1.5">
                    <span className="text-neutral-500 capitalize">{k}</span>
                    <span className={`font-black border px-1.5 py-0.5 text-[10px] ${v >= 80 ? "text-[#D3FF24] border-[#D3FF24]/30" : v >= 60 ? "text-amber-400 border-amber-400/30" : "text-red-400 border-red-400/30"}`}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Strengths</div>
              <ul className="space-y-2">
                {(parsed.strengths || []).map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#D3FF24] mt-0.5 shrink-0" strokeWidth={1.75} /> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Quick Wins</div>
              <ul className="space-y-2">
                {(parsed.quick_wins || []).map((w, i) => (
                  <li key={i} className="text-sm">
                    <span className={`text-[10px] uppercase tracking-wider font-bold mr-2 ${w.impact === "high" ? "text-[#D3FF24]" : "text-neutral-500"}`}>{w.impact}</span>
                    <span className="text-neutral-300">{w.action}</span>
                    <span className="text-neutral-600 ml-2 text-xs">· {w.effort}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {(parsed.keyword_gaps || []).length > 0 && (
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Keyword Gaps</div>
              <div className="flex flex-wrap gap-2">
                {parsed.keyword_gaps.map((k, i) => <span key={i} className="border border-white/15 px-3 py-1 text-xs text-neutral-400">{k}</span>)}
              </div>
            </div>
          )}

          {parsed.recommendations && (
            <div className="border border-white/10 p-5 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">AI Recommendations</div>
              {Object.entries(parsed.recommendations).map(([k, v]) => (
                <div key={k} className="border-l-2 border-[#0055FF] pl-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#D3FF24] font-semibold capitalize">{k}</div>
                  <div className="text-sm text-neutral-300 mt-0.5">{v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Headline ────────────────────────────────────────────────────────────────
function HeadlineSection({ profile }) {
  const [headline, setHeadline] = useState(profile.headline || "");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const [industry, setIndustry] = useState(profile.industry || "");
  const { loading, parsed, run } = useAIStream();

  const optimize = () => {
    if (!headline.trim()) { toast.error("Enter your current headline"); return; }
    run("/api/linkedin/optimize", { section: "headline", content: headline, target_role: targetRole, industry });
  };

  return (
    <div>
      <SectionHeader title="Headline Optimizer" subtitle="Your headline appears in search results — make it a keyword-rich value proposition" />

      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Current Headline <span className="normal-case text-neutral-600 tracking-normal">(max 220 chars)</span></label>
          <div className="relative mt-1">
            <input value={headline} onChange={e => setHeadline(e.target.value)} maxLength={220}
              className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none pr-16"
              placeholder="Software Engineer at Acme Corp" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-neutral-600">{headline.length}/220</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Target Role</label>
            <input value={targetRole} onChange={e => setTargetRole(e.target.value)}
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
              placeholder="Senior Product Manager" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Industry</label>
            <input value={industry} onChange={e => setIndustry(e.target.value)}
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
              placeholder="FinTech / SaaS" />
          </div>
        </div>
        <button onClick={optimize} disabled={loading || !headline.trim()}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Wand2 className="w-4 h-4" /> Generate 5 Headline Variations</>}
        </button>
      </div>

      {loading && !parsed && <Thinking label="Writing powerful headlines…" />}

      {parsed?.headlines && (
        <div className="space-y-4">
          <div className="border border-white/10">
            <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">AI-Generated Headlines</div>
            {parsed.headlines.map((h, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] group">
                <span className="text-[10px] font-black text-neutral-600 w-4">{i + 1}</span>
                <span className="flex-1 text-sm text-white">{h}</span>
                <span className="text-[10px] text-neutral-600">{h.length}/220</span>
                <CopyBtn text={h} />
              </div>
            ))}
          </div>
          {parsed.reasoning && (
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Why These Work</div>
              <p className="text-sm text-neutral-400">{parsed.reasoning}</p>
            </div>
          )}
        </div>
      )}

      <div className="border border-white/10 p-5 bg-[#0A0A0A] mt-6">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Headline Formula Guide</div>
        <div className="space-y-2 text-xs text-neutral-400">
          {[
            ["Formula 1", "[Title] | [Value Prop] | [Industry keyword]"],
            ["Formula 2", "Helping [audience] achieve [outcome] | [Title] at [Company]"],
            ["Formula 3", "[Title] • [Specialty 1] • [Specialty 2] • [Key Achievement]"],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span className="text-[#D3FF24] font-bold shrink-0">{k}:</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── About ───────────────────────────────────────────────────────────────────
function AboutSection({ profile }) {
  const [about, setAbout] = useState(profile.about || "");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const { raw, loading, run } = useAIStream();

  const optimize = () => {
    if (!about.trim()) { toast.error("Enter your current About section"); return; }
    run("/api/linkedin/optimize", { section: "about", content: about, target_role: targetRole });
  };

  return (
    <div>
      <SectionHeader title="About / Summary Optimizer" subtitle="The first 2 lines show before 'See more' — make them your pitch" />
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="border border-white/10 p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Current About Section</div>
            <textarea rows={12} value={about} onChange={e => setAbout(e.target.value)}
              className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
              placeholder="Paste your current About section or write from scratch…" />
            <input value={targetRole} onChange={e => setTargetRole(e.target.value)}
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
              placeholder="Target role (e.g. Head of Product)" />
            <button onClick={optimize} disabled={loading || !about.trim()}
              className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Optimizing…</> : <><Wand2 className="w-4 h-4" /> Optimize About Section</>}
            </button>
          </div>
          <div className="border border-white/10 p-5 bg-[#0A0A0A] space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">About Section Formula</div>
            {[["Hook", "Grab attention in line 1–2"], ["Story", "Your unique background & what drives you"], ["Value", "Skills, specialties, impact"], ["Proof", "1–2 achievements with numbers"], ["CTA", "'Open to…' or 'Let's connect if…'"]].map(([k, v]) => (
              <div key={k} className="flex gap-3 text-xs">
                <span className="text-[#D3FF24] font-bold w-12 shrink-0">{k}</span>
                <span className="text-neutral-500">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 p-5 min-h-[300px]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">AI-Optimized Version</div>
            {raw && <CopyBtn text={raw} />}
          </div>
          {loading && !raw && <div className="flex items-center gap-2 text-neutral-600 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Writing…</div>}
          {raw
            ? <p className="text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed">{raw}</p>
            : !loading && <p className="text-neutral-700 text-sm">Your optimized About section will appear here…</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Experience ──────────────────────────────────────────────────────────────
function ExperienceSection({ profile }) {
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [bullets, setBullets] = useState("");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const { raw, loading, run } = useAIStream();

  const optimize = () => {
    if (!bullets.trim()) { toast.error("Enter your job responsibilities"); return; }
    const content = `Role: ${role} at ${company}\n\nResponsibilities / Achievements:\n${bullets}`;
    run("/api/linkedin/optimize", { section: "experience", content, target_role: targetRole });
  };

  return (
    <div>
      <SectionHeader title="Experience Optimizer" subtitle="Transform duty-based descriptions into achievement-focused bullets with impact metrics" />
      <div className="grid grid-cols-2 gap-6">
        <div className="border border-white/10 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Job Entry</div>
          <div className="grid grid-cols-2 gap-3">
            <input value={role} onChange={e => setRole(e.target.value)}
              className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
              placeholder="Job Title" />
            <input value={company} onChange={e => setCompany(e.target.value)}
              className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
              placeholder="Company" />
          </div>
          <textarea rows={10} value={bullets} onChange={e => setBullets(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder={"• Managed a team of 5 engineers\n• Built backend APIs\n• Reduced load time\n\nWrite your current bullets or plain responsibilities — AI will enhance them."} />
          <input value={targetRole} onChange={e => setTargetRole(e.target.value)}
            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
            placeholder="Target role (for keyword alignment)" />
          <button onClick={optimize} disabled={loading || !bullets.trim()}
            className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enhancing…</> : <><Wand2 className="w-4 h-4" /> Enhance with AI</>}
          </button>
        </div>
        <div className="border border-white/10 p-5 min-h-[300px]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">AI-Enhanced Bullets</div>
            {raw && <CopyBtn text={raw} />}
          </div>
          {loading && !raw && <div className="flex items-center gap-2 text-neutral-600 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Writing…</div>}
          {raw
            ? <p className="text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed">{raw}</p>
            : !loading && <p className="text-neutral-700 text-sm">Enhanced bullets will appear here…</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Skills ──────────────────────────────────────────────────────────────────
function SkillsSection({ profile, setProfile }) {
  const [input, setInput] = useState("");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const { loading, parsed, run } = useAIStream();

  const addSkills = () => {
    const news = input.split(",").map(s => s.trim()).filter(Boolean);
    setProfile(p => ({ ...p, skills: [...new Set([...(p.skills || []), ...news])] }));
    setInput("");
  };
  const removeSkill = s => setProfile(p => ({ ...p, skills: (p.skills || []).filter(x => x !== s) }));
  const addSuggested = s => setProfile(p => ({ ...p, skills: [...new Set([...(p.skills || []), s])] }));

  const analyze = () => {
    const content = (profile.skills || []).join(", ");
    if (!content) { toast.error("Add some skills first"); return; }
    run("/api/linkedin/optimize", { section: "skills", content, target_role: targetRole, industry: profile.industry });
  };

  return (
    <div>
      <SectionHeader title="Skills Optimizer" subtitle="Top 50 skills = 5× more recruiter messages. Let AI close the gap." />

      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Your Current Skills</label>
          <div className="flex flex-wrap gap-2 mt-2 min-h-[44px] p-2 border border-white/5">
            {(profile.skills || []).map(s => (
              <span key={s} className="flex items-center gap-1.5 border border-white/15 px-3 py-1 text-xs">
                {s}
                <button onClick={() => removeSkill(s)} className="text-neutral-500 hover:text-red-400"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {!(profile.skills || []).length && <span className="text-xs text-neutral-600">No skills added yet</span>}
          </div>
          <div className="flex gap-2 mt-2">
            <input value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") addSkills(); }}
              className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
              placeholder="Add skills (comma-separated, then Enter)" />
            <button onClick={addSkills} className="border border-white/15 px-4 text-sm hover:border-[#0055FF] transition">Add</button>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Target Role</label>
          <input value={targetRole} onChange={e => setTargetRole(e.target.value)}
            className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
            placeholder="e.g. Data Scientist, Product Manager" />
        </div>
        <button onClick={analyze} disabled={loading || !(profile.skills || []).length}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><Sparkles className="w-4 h-4" /> Analyze & Optimize Skills</>}
        </button>
      </div>

      {loading && !parsed && <Thinking label="Analyzing skill gaps…" />}

      {parsed && (
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-white/10 p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-3">Prioritize These</div>
            <ul className="space-y-2">
              {(parsed.prioritize || []).map((s, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className="text-neutral-600 text-xs w-4">{i + 1}</span>
                  <span className="text-white">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-white/10 p-5">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold mb-3">Add These Skills</div>
            <ul className="space-y-2">
              {(parsed.add || []).map((s, i) => (
                <li key={i} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-neutral-300">{s}</span>
                  <button onClick={() => addSuggested(s)} className="text-[10px] text-[#D3FF24] border border-[#D3FF24]/30 px-2 py-0.5 hover:bg-[#D3FF24]/10 transition">+ Add</button>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-white/10 p-5">
            <div className="text-[10px] uppercase tracking-widest text-red-400 font-semibold mb-3">Consider Removing</div>
            <ul className="space-y-2">
              {(parsed.remove || []).map((s, i) => <li key={i} className="text-sm text-neutral-500 line-through">{s}</li>)}
            </ul>
            {parsed.reasoning && <p className="text-xs text-neutral-600 mt-3">{parsed.reasoning}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ATS Checker ─────────────────────────────────────────────────────────────
function ATSSection({ profile }) {
  const [text, setText] = useState("");
  const { loading, parsed, run } = useAIStream();

  const check = () => {
    const t = text.trim() || [profile.headline, profile.about, (profile.skills || []).join(", ")].filter(Boolean).join("\n\n");
    if (!t) { toast.error("Add profile text or fill out the Overview section first"); return; }
    run("/api/linkedin/ats-check", { profile_text: t });
  };

  const pc = { critical: "text-red-400", high: "text-amber-400", medium: "text-[#D3FF24]", low: "text-neutral-500" };

  return (
    <div>
      <SectionHeader title="ATS Checker" subtitle="Check if your profile passes Applicant Tracking Systems used by 99% of Fortune 500s" />

      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Profile Text <span className="normal-case text-neutral-600 tracking-normal">(or auto-uses saved profile data)</span></label>
          <textarea rows={6} value={text} onChange={e => setText(e.target.value)}
            className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Paste your LinkedIn profile or leave blank to use saved data…" />
        </div>
        <button onClick={check} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</> : <><SearchCheck className="w-4 h-4" /> Run ATS Check</>}
        </button>
      </div>

      {loading && !parsed && <Thinking label="Scanning for ATS compatibility…" />}

      {parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">ATS Score</div>
              <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                  <ScoreRing score={parsed.ats_score} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-black">{parsed.ats_score}</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-bold">{parsed.ats_score >= 80 ? "ATS-Ready" : parsed.ats_score >= 60 ? "Needs Work" : "Critical Issues"}</div>
                  <div className="text-xs text-neutral-500 mt-1">Density: <span className="text-white">{parsed.keyword_density}</span></div>
                </div>
              </div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Keywords Present</div>
              <div className="flex flex-wrap gap-1.5">
                {(parsed.top_keywords_present || []).map((k, i) => <span key={i} className="border border-[#D3FF24]/30 text-[#D3FF24] text-[10px] px-2 py-0.5">{k}</span>)}
              </div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Missing Keywords</div>
              <div className="flex flex-wrap gap-1.5">
                {(parsed.keywords_to_add || []).map((k, i) => <span key={i} className="border border-red-400/30 text-red-400 text-[10px] px-2 py-0.5">{k}</span>)}
              </div>
            </div>
          </div>

          {(parsed.improvements || []).length > 0 && (
            <div className="border border-white/10">
              <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Improvements</div>
              <div className="divide-y divide-white/5">
                {parsed.improvements.map((imp, i) => (
                  <div key={i} className="flex items-start gap-4 px-5 py-4">
                    <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0 w-14 ${pc[imp.priority]}`}>{imp.priority}</span>
                    <div>
                      <div className="text-sm font-medium text-white">{imp.issue}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{imp.fix}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {parsed.summary && <div className="border border-white/10 p-5"><div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Summary</div><p className="text-sm text-neutral-300">{parsed.summary}</p></div>}
        </div>
      )}
    </div>
  );
}

// ─── Job Match ───────────────────────────────────────────────────────────────
function JobMatchSection({ profile }) {
  const [profileText, setProfileText] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const { loading, parsed, run } = useAIStream();

  const match = () => {
    const pt = profileText.trim() || [profile.name, profile.headline, profile.about, (profile.skills || []).join(", ")].filter(Boolean).join("\n");
    if (!jobDesc.trim()) { toast.error("Paste the job description"); return; }
    if (!pt) { toast.error("Enter profile text or fill out the Overview first"); return; }
    run("/api/linkedin/job-match", { profile_text: pt, job_description: jobDesc });
  };

  const vc = { "Strong Match": "text-[#D3FF24]", "Good Match": "text-green-400", "Partial Match": "text-amber-400", "Weak Match": "text-red-400" };

  return (
    <div>
      <SectionHeader title="Job Match Analyzer" subtitle="See how well your LinkedIn profile matches a job description — before applying" />

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="border border-white/10 p-5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Your LinkedIn Profile</div>
          <textarea rows={8} value={profileText} onChange={e => setProfileText(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Paste your profile (or leave blank to use saved data)…" />
        </div>
        <div className="border border-white/10 p-5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Job Description</div>
          <textarea rows={8} value={jobDesc} onChange={e => setJobDesc(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Paste the full job description here…" />
        </div>
      </div>

      <button onClick={match} disabled={loading || !jobDesc.trim()}
        className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50 mb-6">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Matching…</> : <><Target className="w-4 h-4" /> Analyze Job Match</>}
      </button>

      {loading && !parsed && <Thinking label="Calculating match score…" />}

      {parsed && (
        <div className="space-y-4">
          <div className="border border-white/10 p-6 flex items-center gap-8">
            <div className="relative shrink-0">
              <ScoreRing score={parsed.match_score} size={100} sw={10} />
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className="text-2xl font-black">{parsed.match_score}%</span>
              </div>
            </div>
            <div>
              <div className={`text-2xl font-black ${vc[parsed.verdict] || "text-white"}`}>{parsed.verdict}</div>
              <div className="text-sm text-neutral-400 mt-1">Experience fit: <span className="text-white capitalize">{parsed.experience_match}</span></div>
              {parsed.application_advice && <p className="text-xs text-neutral-500 mt-2 max-w-md">{parsed.application_advice}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-3">Matching Keywords</div>
              <div className="flex flex-wrap gap-2">{(parsed.matching_keywords || []).map((k, i) => <span key={i} className="border border-[#D3FF24]/30 text-[#D3FF24] text-xs px-2 py-1">{k}</span>)}</div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-red-400 font-semibold mb-3">Missing Keywords</div>
              <div className="flex flex-wrap gap-2">{(parsed.missing_keywords || []).map((k, i) => <span key={i} className="border border-red-400/30 text-red-400 text-xs px-2 py-1">{k}</span>)}</div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-green-400 font-semibold mb-3">Strengths for This Role</div>
              <ul className="space-y-1.5">{(parsed.strengths_for_role || []).map((s, i) => <li key={i} className="flex gap-2 text-xs text-neutral-300"><CheckCircle2 className="w-3 h-3 text-green-400 mt-0.5 shrink-0" />{s}</li>)}</ul>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold mb-3">How to Tailor Your Profile</div>
              <ul className="space-y-1.5">{(parsed.how_to_tailor || []).map((s, i) => <li key={i} className="flex gap-2 text-xs text-neutral-300"><ArrowRight className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />{s}</li>)}</ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Content Studio ──────────────────────────────────────────────────────────
function ContentStudioSection({ profile }) {
  const [contentType, setContentType] = useState("thought leadership");
  const { loading, parsed, run } = useAIStream();

  const types = ["thought leadership", "personal story", "industry insight", "tips & tricks", "case study", "career advice"];
  const fmtColor = { carousel: "text-[#0055FF]", story: "text-purple-400", list: "text-[#D3FF24]", question: "text-amber-400", insight: "text-green-400", "how-to": "text-red-400" };

  const generate = () => {
    if (!profile.headline) { toast.error("Add your headline in Overview first"); return; }
    run("/api/linkedin/content-ideas", { headline: profile.headline, about: profile.about || "", industry: profile.industry || "", content_type: contentType });
  };

  return (
    <div>
      <SectionHeader title="Content Studio" subtitle="Generate LinkedIn posts that build your brand and attract opportunities" />

      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Content Type</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {types.map(t => (
              <button key={t} onClick={() => setContentType(t)}
                className={`px-3 py-1.5 text-xs border transition capitalize ${contentType === t ? "border-[#D3FF24] text-[#D3FF24] bg-[#D3FF24]/5" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        {!profile.headline && <p className="text-xs text-amber-400 border border-amber-500/20 p-3 bg-amber-500/5">Add your headline in <strong>Overview</strong> to enable content generation.</p>}
        <button onClick={generate} disabled={loading || !profile.headline}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><PenTool className="w-4 h-4" /> Generate 6 Post Ideas</>}
        </button>
      </div>

      {loading && !parsed && <Thinking label="Creating content ideas…" />}

      {parsed?.posts && (
        <div className="space-y-4">
          {parsed.content_pillars && (
            <div className="border border-white/10 p-4 flex items-center gap-4 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold shrink-0">Content Pillars</span>
              {parsed.content_pillars.map((p, i) => <span key={i} className="border border-[#0055FF]/30 text-[#0055FF] text-xs px-3 py-1">{p}</span>)}
            </div>
          )}
          {parsed.posts.map((post, i) => (
            <div key={i} className="border border-white/10">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 border-current/30 ${fmtColor[post.format] || "text-neutral-400"}`}>{post.format}</span>
                  <span className={`text-[10px] uppercase tracking-wider ${post.expected_engagement === "viral" ? "text-[#D3FF24]" : post.expected_engagement === "high" ? "text-green-400" : "text-neutral-500"}`}>{post.expected_engagement} engagement</span>
                </div>
                <CopyBtn text={post.full_post} />
              </div>
              <div className="px-5 py-4">
                <p className="text-sm font-semibold text-white mb-2">{post.hook}</p>
                <p className="text-xs text-neutral-400 whitespace-pre-wrap line-clamp-4">{post.full_post}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(post.hashtags || []).map((h, j) => <span key={j} className="text-[10px] text-[#0055FF]">{h.startsWith("#") ? h : `#${h}`}</span>)}
                </div>
              </div>
            </div>
          ))}
          {parsed.posting_schedule && (
            <div className="border border-white/10 p-4 text-sm text-neutral-400">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mr-2">Posting Schedule:</span>
              {parsed.posting_schedule}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Education ───────────────────────────────────────────────────────────────
function EducationSection({ profile }) {
  const [school, setSchool] = useState("");
  const [degree, setDegree] = useState("");
  const [field, setField] = useState("");
  const [dates, setDates] = useState("");
  const [desc, setDesc] = useState("");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const { raw, loading, run } = useAIStream();

  const optimize = () => {
    if (!school.trim()) { toast.error("Enter school name"); return; }
    const content = `School: ${school}\nDegree: ${degree} in ${field}\nDates: ${dates}\nDescription: ${desc}`;
    run("/api/linkedin/optimize", { section: "education", content, target_role: targetRole });
  };

  return (
    <div>
      <SectionHeader title="Education Optimizer" subtitle="AI-enhanced education descriptions that highlight what recruiters actually care about" />
      <div className="grid grid-cols-2 gap-6">
        <div className="border border-white/10 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Education Entry</div>
          <div className="grid grid-cols-2 gap-3">
            <input value={school} onChange={e => setSchool(e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="University / School" />
            <input value={degree} onChange={e => setDegree(e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="B.S. / M.S. / MBA" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={field} onChange={e => setField(e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Field of Study" />
            <input value={dates} onChange={e => setDates(e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="2018 – 2022" />
          </div>
          <textarea rows={5} value={desc} onChange={e => setDesc(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Activities, GPA, honors, relevant coursework…" />
          <input value={targetRole} onChange={e => setTargetRole(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Target role (for keyword alignment)" />
          <button onClick={optimize} disabled={loading || !school.trim()}
            className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enhancing…</> : <><Wand2 className="w-4 h-4" /> Enhance with AI</>}
          </button>
        </div>
        <div className="space-y-4">
          <div className="border border-white/10 p-5 min-h-[260px]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">AI-Enhanced Description</div>
              {raw && <CopyBtn text={raw} />}
            </div>
            {loading && !raw && <div className="flex items-center gap-2 text-neutral-600 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Writing…</div>}
            {raw ? <p className="text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed">{raw}</p>
              : !loading && <p className="text-neutral-700 text-sm">Enhanced description will appear here…</p>}
          </div>
          <div className="border border-white/10 p-5 bg-[#0A0A0A] space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">What Recruiters Want to See</div>
            {[["Relevant Coursework", "4–6 courses matching your target role"], ["Academic Projects", "Capstone/thesis with quantified outcomes"], ["Honors & Awards", "Dean's list, scholarships, distinctions"], ["Activities", "Clubs, leadership, hackathons, research"]].map(([k, v]) => (
              <div key={k} className="flex gap-3 text-xs"><span className="text-[#D3FF24] font-bold w-36 shrink-0">{k}</span><span className="text-neutral-500">{v}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Projects ────────────────────────────────────────────────────────────────
function ProjectsSection({ profile }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [stack, setStack] = useState("");
  const [impact, setImpact] = useState("");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const { raw, loading, run } = useAIStream();

  const optimize = () => {
    if (!desc.trim()) { toast.error("Enter project description"); return; }
    const content = `Project: ${name}\nDescription: ${desc}\nTech Stack: ${stack}\nImpact / Results: ${impact}`;
    run("/api/linkedin/optimize", { section: "projects", content, target_role: targetRole });
  };

  return (
    <div>
      <SectionHeader title="Projects Optimizer" subtitle="Showcase your best work with AI-enhanced descriptions that attract technical recruiters" />
      <div className="grid grid-cols-2 gap-6">
        <div className="border border-white/10 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Project Entry</div>
          <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Project Name" />
          <textarea rows={4} value={desc} onChange={e => setDesc(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="What did you build? What problem did it solve?" />
          <input value={stack} onChange={e => setStack(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Tech stack (React, Node.js, AWS…)" />
          <input value={impact} onChange={e => setImpact(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Impact / results (users, revenue, performance…)" />
          <input value={targetRole} onChange={e => setTargetRole(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Target role" />
          <button onClick={optimize} disabled={loading || !desc.trim()}
            className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enhancing…</> : <><Wand2 className="w-4 h-4" /> Enhance Bullets</>}
          </button>
        </div>
        <div className="border border-white/10 p-5 min-h-[300px]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">AI-Enhanced Bullets</div>
            {raw && <CopyBtn text={raw} />}
          </div>
          {loading && !raw && <div className="flex items-center gap-2 text-neutral-600 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Writing…</div>}
          {raw ? <p className="text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed">{raw}</p>
            : !loading && <p className="text-neutral-700 text-sm">Enhanced project bullets will appear here…</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Certifications ──────────────────────────────────────────────────────────
function CertificationsSection({ profile }) {
  const [currentRole, setCurrentRole] = useState(profile.target_role || "");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const [skills, setSkills] = useState((profile.skills || []).join(", "));
  const [industry, setIndustry] = useState(profile.industry || "");
  const { loading, parsed, run } = useAIStream();

  const recommend = () => {
    run("/api/linkedin/certifications", { current_role: currentRole, target_role: targetRole, skills, industry });
  };

  const ic = { high: "text-[#D3FF24] border-[#D3FF24]/30", medium: "text-amber-400 border-amber-400/30", low: "text-neutral-400 border-neutral-400/30" };

  return (
    <div>
      <SectionHeader title="Certification Recommender" subtitle="AI-curated certifications to boost credibility and recruiter visibility" />
      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Current Role</label>
            <input value={currentRole} onChange={e => setCurrentRole(e.target.value)} className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Software Engineer" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Target Role</label>
            <input value={targetRole} onChange={e => setTargetRole(e.target.value)} className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Senior Engineer / Architect" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Current Skills</label>
            <input value={skills} onChange={e => setSkills(e.target.value)} className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Python, AWS, React…" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Industry</label>
            <input value={industry} onChange={e => setIndustry(e.target.value)} className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="FinTech, SaaS, Healthcare…" /></div>
        </div>
        <button onClick={recommend} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Award className="w-4 h-4" /> Recommend Certifications</>}
        </button>
      </div>
      {loading && !parsed && <Thinking label="Finding the best certifications for your path…" />}
      {parsed && (
        <div className="space-y-4">
          {parsed.learning_path && <div className="border border-[#0055FF]/20 bg-[#0055FF]/5 px-5 py-3 text-sm text-neutral-300"><span className="text-[10px] uppercase tracking-widest text-[#0055FF] font-semibold mr-2">Learning Path:</span>{parsed.learning_path}</div>}
          <div className="space-y-3">
            {(parsed.top_recommendations || []).map((cert, i) => (
              <div key={i} className="border border-white/10 p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="font-semibold text-white">{cert.name}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{cert.provider}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 ${ic[cert.impact] || ic.medium}`}>{cert.impact} impact</span>
                  </div>
                </div>
                <p className="text-sm text-neutral-400 mb-3">{cert.why}</p>
                <div className="flex items-center gap-4 text-xs text-neutral-600">
                  <span>⏱ {cert.time_to_complete}</span>
                  <span>💰 {cert.cost}</span>
                </div>
                {(cert.skills_gained || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {cert.skills_gained.map((s, j) => <span key={j} className="border border-white/10 text-neutral-400 text-[10px] px-2 py-0.5">{s}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
          {(parsed.quick_wins || []).length > 0 && (
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Quick Wins (Free/Fast)</div>
              <ul className="space-y-2">{parsed.quick_wins.map((w, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><CheckCircle2 className="w-3.5 h-3.5 text-[#D3FF24] mt-0.5 shrink-0" />{w}</li>)}</ul>
            </div>
          )}
          {parsed.industry_note && <div className="border border-white/10 p-4 text-xs text-neutral-500"><span className="text-[#D3FF24] font-semibold">Industry note: </span>{parsed.industry_note}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Recommendations ─────────────────────────────────────────────────────────
function RecommendationsSection({ profile }) {
  const [relType, setRelType] = useState("manager");
  const [context, setContext] = useState("");
  const [targetRole, setTargetRole] = useState(profile.target_role || "");
  const { loading, parsed, run } = useAIStream();

  const generate = () => {
    if (!context.trim()) { toast.error("Describe your work context / relationship"); return; }
    run("/api/linkedin/optimize", { section: "recommendations", content: `Relationship: ${relType}\nContext: ${context}`, target_role: targetRole });
  };

  const relTypes = ["manager", "peer", "report", "client", "mentor"];

  return (
    <div>
      <SectionHeader title="Recommendations" subtitle="AI templates to request and write powerful LinkedIn recommendations" />
      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Relationship Type</label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {relTypes.map(r => (
              <button key={r} onClick={() => setRelType(r)}
                className={`px-3 py-1.5 text-xs border transition capitalize ${relType === r ? "border-[#D3FF24] text-[#D3FF24] bg-[#D3FF24]/5" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Work Context</label>
          <textarea rows={4} value={context} onChange={e => setContext(e.target.value)}
            className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Describe the project you worked on together, your role, impact, and what you want highlighted…" />
        </div>
        <input value={targetRole} onChange={e => setTargetRole(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="Target role you're positioning for" />
        <button onClick={generate} disabled={loading || !context.trim()}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><MessageSquare className="w-4 h-4" /> Generate Templates</>}
        </button>
      </div>
      {loading && !parsed && <Thinking label="Writing recommendation templates…" />}
      {parsed && (
        <div className="space-y-4">
          <div className="border border-white/10">
            <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Request Templates</div>
            <div className="divide-y divide-white/5">
              {(parsed.request_templates || []).map((t, i) => (
                <div key={i} className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold capitalize">To: {t.to}</span>
                    <CopyBtn text={t.template} />
                  </div>
                  <p className="text-sm text-neutral-300 whitespace-pre-wrap">{t.template}</p>
                </div>
              ))}
            </div>
          </div>
          {parsed.example_recommendation && (
            <div className="border border-white/10 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Example Recommendation to Share</div>
                <CopyBtn text={parsed.example_recommendation} />
              </div>
              <p className="text-sm text-neutral-300 italic leading-relaxed">"{parsed.example_recommendation}"</p>
            </div>
          )}
          {(parsed.key_points_to_highlight || []).length > 0 && (
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Key Points to Highlight</div>
              <ul className="space-y-1.5">{parsed.key_points_to_highlight.map((p, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><ArrowRight className="w-3.5 h-3.5 text-[#D3FF24] mt-0.5 shrink-0" />{p}</li>)}</ul>
            </div>
          )}
          {(parsed.tips || []).length > 0 && (
            <div className="border border-white/10 p-5 bg-[#0A0A0A]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Tips</div>
              <ul className="space-y-1.5">{parsed.tips.map((t, i) => <li key={i} className="text-sm text-neutral-400">· {t}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Featured ────────────────────────────────────────────────────────────────
function FeaturedSection({ profile }) {
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState("");
  const { loading, parsed, run } = useAIStream();

  const types = ["Published articles", "LinkedIn posts", "Portfolio website", "GitHub projects", "Case studies", "Videos / demos", "Presentations", "Certifications", "Media mentions"];
  const toggle = t => setSelected(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);

  const generate = () => {
    const content = `Available content: ${selected.join(", ") || "general"}\nContext: ${extra}`;
    run("/api/linkedin/optimize", { section: "featured", content, target_role: profile.target_role, industry: profile.industry });
  };

  return (
    <div>
      <SectionHeader title="Featured Section" subtitle="Curate your best work — AI strategy for what to pin and how to order it" />
      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Content You Have Available</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {types.map(t => (
              <button key={t} onClick={() => toggle(t)}
                className={`px-3 py-1.5 text-xs border transition ${selected.includes(t) ? "border-[#D3FF24] text-[#D3FF24] bg-[#D3FF24]/5" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <textarea rows={3} value={extra} onChange={e => setExtra(e.target.value)}
          className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
          placeholder="Any extra context — your goals, target audience, biggest achievements…" />
        <button onClick={generate} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Strategizing…</> : <><Star className="w-4 h-4" /> Build Featured Strategy</>}
        </button>
      </div>
      {loading && !parsed && <Thinking label="Building your Featured strategy…" />}
      {parsed && (
        <div className="space-y-4">
          {parsed.strategy && <div className="border border-[#D3FF24]/20 bg-[#D3FF24]/[0.03] p-5"><div className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-2">Strategy</div><p className="text-sm text-neutral-300">{parsed.strategy}</p></div>}
          {(parsed.recommended_content_types || []).length > 0 && (
            <div className="border border-white/10">
              <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Recommended Content (in order)</div>
              <div className="divide-y divide-white/5">
                {parsed.recommended_content_types.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-5">
                    <span className="font-mono text-[#D3FF24] font-black text-sm shrink-0">0{i + 1}</span>
                    <div>
                      <div className="text-sm font-semibold text-white">{item.type}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{item.why}</div>
                      <div className="text-xs text-neutral-600 mt-1 italic">{item.example}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(parsed.content_ideas || []).length > 0 && (
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Content Ideas to Create</div>
              <ul className="space-y-2">{parsed.content_ideas.map((idea, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><ArrowRight className="w-3.5 h-3.5 text-[#0055FF] mt-0.5 shrink-0" />{idea}</li>)}</ul>
            </div>
          )}
          {(parsed.avoid || []).length > 0 && (
            <div className="border border-red-500/20 bg-red-500/[0.03] p-5">
              <div className="text-[10px] uppercase tracking-widest text-red-400 font-semibold mb-3">Avoid</div>
              <ul className="space-y-1.5">{parsed.avoid.map((a, i) => <li key={i} className="text-sm text-neutral-400">✕ {a}</li>)}</ul>
            </div>
          )}
          {parsed.ordering_advice && <div className="border border-white/10 p-4 text-xs text-neutral-500"><span className="text-white font-semibold">Ordering: </span>{parsed.ordering_advice}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Resume Sync ─────────────────────────────────────────────────────────────
function ResumeSyncSection() {
  const [resumeText, setResumeText] = useState("");
  const [linkedinText, setLinkedinIconText] = useState("");
  const { loading, parsed, run } = useAIStream();

  const sync = () => {
    if (!resumeText.trim()) { toast.error("Paste your resume text"); return; }
    run("/api/linkedin/resume-sync", { resume_text: resumeText, linkedin_text: linkedinText });
  };

  const scoreColor = s => s >= 80 ? "text-[#D3FF24]" : s >= 60 ? "text-amber-400" : "text-red-400";

  return (
    <div>
      <SectionHeader title="Resume Sync" subtitle="AI cross-checks your resume and LinkedIn for inconsistencies recruiters will catch" />
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="border border-white/10 p-5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Resume Text <span className="normal-case text-neutral-700">(required)</span></div>
          <textarea rows={10} value={resumeText} onChange={e => setResumeText(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Paste your full resume text here…" />
        </div>
        <div className="border border-white/10 p-5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">LinkedIn Profile Text <span className="normal-case text-neutral-700">(optional)</span></div>
          <textarea rows={10} value={linkedinText} onChange={e => setLinkedinIconText(e.target.value)}
            className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Paste your LinkedIn profile text (Ctrl+A on your profile page)…" />
        </div>
      </div>
      <button onClick={sync} disabled={loading || !resumeText.trim()}
        className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50 mb-6">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Comparing…</> : <><ScrollText className="w-4 h-4" /> Sync Check</>}
      </button>
      {loading && !parsed && <Thinking label="Comparing your resume and LinkedIn…" />}
      {parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-white/10 p-5 text-center">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Consistency Score</div>
              <div className={`text-4xl font-black ${scoreColor(parsed.consistency_score)}`}>{parsed.consistency_score}</div>
            </div>
            <div className="border border-white/10 p-5 text-center">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Keyword Overlap</div>
              <div className={`text-4xl font-black ${scoreColor(parsed.keyword_overlap_pct)}`}>{parsed.keyword_overlap_pct}%</div>
            </div>
            <div className="border border-white/10 p-5 text-center">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Tone Alignment</div>
              <div className="text-lg font-black text-white capitalize mt-2">{(parsed.tone_alignment || "").replace(/_/g, " ")}</div>
            </div>
          </div>
          {(parsed.inconsistencies || []).length > 0 && (
            <div className="border border-white/10">
              <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-red-400">Inconsistencies Found</div>
              <div className="divide-y divide-white/5">
                {parsed.inconsistencies.map((inc, i) => (
                  <div key={i} className="p-5">
                    <div className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2">{inc.field}</div>
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div className="bg-green-500/5 border border-green-500/20 p-2 rounded"><div className="text-[9px] text-green-400 uppercase font-bold mb-1">Resume</div><div className="text-xs text-neutral-300">{inc.resume_says}</div></div>
                      <div className="bg-amber-500/5 border border-amber-500/20 p-2 rounded"><div className="text-[9px] text-amber-400 uppercase font-bold mb-1">LinkedIn</div><div className="text-xs text-neutral-300">{inc.linkedin_says}</div></div>
                    </div>
                    <div className="text-xs text-[#D3FF24]">→ {inc.fix}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(parsed.action_items || []).length > 0 && (
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Action Items</div>
              <ul className="space-y-2">{parsed.action_items.map((a, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><span className="text-[#D3FF24] font-bold shrink-0">{i + 1}.</span>{a}</li>)}</ul>
            </div>
          )}
          {parsed.summary && <div className="border border-white/10 p-5"><div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Summary</div><p className="text-sm text-neutral-300">{parsed.summary}</p></div>}
        </div>
      )}
    </div>
  );
}

// ─── Networking ──────────────────────────────────────────────────────────────
function NetworkingSection({ profile }) {
  const [targetType, setTargetType] = useState("recruiter");
  const [context, setContext] = useState("");
  const { loading, parsed, run } = useAIStream();

  const generate = () => {
    if (!context.trim()) { toast.error("Describe your context"); return; }
    const content = `Target: ${targetType}\nContext: ${context}`;
    run("/api/linkedin/optimize", { section: "networking", content, target_role: profile.target_role, industry: profile.industry });
  };

  const targetTypes = ["recruiter", "hiring_manager", "peer", "alumni", "industry_leader", "vc_investor"];

  return (
    <div>
      <SectionHeader title="Networking Templates" subtitle="Personalized outreach messages that get responses — not ignored" />
      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Who Are You Reaching Out To?</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {targetTypes.map(t => (
              <button key={t} onClick={() => setTargetType(t)}
                className={`px-3 py-1.5 text-xs border transition capitalize ${targetType === t ? "border-[#D3FF24] text-[#D3FF24] bg-[#D3FF24]/5" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
                {t.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Your Context</label>
          <textarea rows={4} value={context} onChange={e => setContext(e.target.value)}
            className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="What role/company are you targeting? Why are you reaching out? Any mutual connections or context?" />
        </div>
        <button onClick={generate} disabled={loading || !context.trim()}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing…</> : <><Network className="w-4 h-4" /> Generate Templates</>}
        </button>
      </div>
      {loading && !parsed && <Thinking label="Writing personalized outreach templates…" />}
      {parsed && (
        <div className="space-y-4">
          <div className="border border-white/10">
            <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Connection Request Templates</div>
            <div className="divide-y divide-white/5">
              {(parsed.connection_requests || []).map((cr, i) => (
                <div key={i} className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold capitalize">{(cr.type || "").replace(/_/g, " ")}</span>
                      <span className="text-[10px] text-neutral-600 ml-3">{cr.when_to_use}</span>
                    </div>
                    <CopyBtn text={cr.template} />
                  </div>
                  <p className="text-sm text-neutral-300 whitespace-pre-wrap">{cr.template}</p>
                </div>
              ))}
            </div>
          </div>
          {parsed.follow_up_message && (
            <div className="border border-white/10 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Follow-Up Message</div>
                <CopyBtn text={parsed.follow_up_message} />
              </div>
              <p className="text-sm text-neutral-300">{parsed.follow_up_message}</p>
            </div>
          )}
          {parsed.inmail_template && (
            <div className="border border-[#0055FF]/20 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-[#0055FF] font-semibold">InMail Template <span className="text-neutral-600">(Premium)</span></div>
                <CopyBtn text={parsed.inmail_template} />
              </div>
              <p className="text-sm text-neutral-300 whitespace-pre-wrap">{parsed.inmail_template}</p>
            </div>
          )}
          {(parsed.networking_tips || []).length > 0 && (
            <div className="border border-white/10 p-5 bg-[#0A0A0A]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Tips</div>
              <ul className="space-y-1.5">{parsed.networking_tips.map((t, i) => <li key={i} className="text-sm text-neutral-400">· {t}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Recruiter Mode ──────────────────────────────────────────────────────────
function RecruiterModeSection({ profile }) {
  const [profileText, setProfileText] = useState("");
  const { loading, parsed, run } = useAIStream();

  const analyze = () => {
    const pt = profileText.trim() || [profile.name, profile.headline, profile.about, (profile.skills || []).join(", ")].filter(Boolean).join("\n\n");
    if (!pt) { toast.error("Add profile data in Overview or paste text below"); return; }
    run("/api/linkedin/optimize", { section: "recruiter", content: pt, target_role: profile.target_role, industry: profile.industry });
  };

  const decisionColor = { Yes: "text-[#D3FF24]", Maybe: "text-amber-400", No: "text-red-400" };

  return (
    <div>
      <SectionHeader title="Recruiter Mode" subtitle="See your profile exactly as a recruiter would — would they shortlist you?" />
      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Profile Text <span className="normal-case text-neutral-600">(auto-uses saved data if blank)</span></label>
          <textarea rows={6} value={profileText} onChange={e => setProfileText(e.target.value)}
            className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Paste your LinkedIn profile or leave blank to use saved Overview data…" />
        </div>
        <button onClick={analyze} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><Eye className="w-4 h-4" /> View Through Recruiter Eyes</>}
        </button>
      </div>
      {loading && !parsed && <Thinking label="Simulating recruiter evaluation…" />}
      {parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Shortlist Decision</div>
              <div className={`text-3xl font-black ${decisionColor[parsed.shortlist_decision] || "text-white"}`}>{parsed.shortlist_decision}</div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">First Impression</div>
              <div className="relative shrink-0 flex items-center gap-3">
                <ScoreRing score={parsed.first_impression_score || 0} />
                <div className="absolute left-0 inset-y-0 flex items-center justify-center w-[80px]">
                  <span className="text-lg font-black">{parsed.first_impression_score}</span>
                </div>
              </div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Would Shortlist For</div>
              <ul className="space-y-1">{(parsed.would_shortlist_for || []).map((r, i) => <li key={i} className="text-xs text-neutral-300">· {r}</li>)}</ul>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-3">Search Factors — Positive</div>
              <ul className="space-y-1.5">{(parsed.search_rank_factors?.positive || []).map((f, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><CheckCircle2 className="w-3.5 h-3.5 text-[#D3FF24] mt-0.5 shrink-0" />{f}</li>)}</ul>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-red-400 font-semibold mb-3">Red Flags</div>
              <ul className="space-y-1.5">{(parsed.red_flags || []).map((f, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><X className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />{f}</li>)}</ul>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#0055FF] font-semibold mb-3">Keywords Found</div>
              <div className="flex flex-wrap gap-1.5">{(parsed.keywords_found || []).map((k, i) => <span key={i} className="border border-[#0055FF]/30 text-[#0055FF] text-[10px] px-2 py-0.5">{k}</span>)}</div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold mb-3">Keywords Missing</div>
              <div className="flex flex-wrap gap-1.5">{(parsed.keywords_missing || []).map((k, i) => <span key={i} className="border border-amber-400/30 text-amber-400 text-[10px] px-2 py-0.5">{k}</span>)}</div>
            </div>
          </div>
          {(parsed.profile_improvements || []).length > 0 && (
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Top Improvements</div>
              <ul className="space-y-2">{parsed.profile_improvements.map((imp, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><span className="text-[#D3FF24] font-bold shrink-0">{i + 1}.</span>{imp}</li>)}</ul>
            </div>
          )}
          {parsed.inmail_reason && <div className="border border-white/10 p-5 bg-[#0A0A0A]"><div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Why A Recruiter Would InMail You</div><p className="text-sm text-neutral-300">{parsed.inmail_reason}</p></div>}
        </div>
      )}
    </div>
  );
}

// ─── Analytics ───────────────────────────────────────────────────────────────
function AnalyticsSection({ profile }) {
  const [profileViews, setProfileViews] = useState("");
  const [searchAppearances, setSearchAppearances] = useState("");
  const [ssiScore, setSsiScore] = useState("");
  const { loading, parsed, run } = useAIStream();

  const analyze = () => {
    run("/api/linkedin/analytics-tips", {
      profile_views: profileViews,
      search_appearances: searchAppearances,
      ssi_score: ssiScore,
      headline: profile.headline,
      industry: profile.industry,
      target_role: profile.target_role,
    });
  };

  const gradeColor = { A: "text-[#D3FF24]", B: "text-green-400", C: "text-amber-400", D: "text-orange-400", F: "text-red-400" };

  return (
    <div>
      <SectionHeader title="Analytics" subtitle="Enter your LinkedIn stats — AI turns numbers into a growth action plan" />
      <div className="border border-[#0055FF]/20 bg-[#0055FF]/[0.03] p-4 mb-6 text-xs text-neutral-400">
        <span className="text-[#0055FF] font-semibold">Where to find these: </span>
        LinkedIn → Dashboard (below your profile photo) → Profile views &amp; Search appearances. SSI: linkedin.com/sales/ssi
      </div>
      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Profile Views (weekly)</label>
            <input value={profileViews} onChange={e => setProfileViews(e.target.value)} type="number"
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="e.g. 47" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Search Appearances (weekly)</label>
            <input value={searchAppearances} onChange={e => setSearchAppearances(e.target.value)} type="number"
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="e.g. 230" /></div>
          <div><label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">SSI Score</label>
            <input value={ssiScore} onChange={e => setSsiScore(e.target.value)} type="number" max="100"
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" placeholder="e.g. 65" /></div>
        </div>
        <button onClick={analyze} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><TrendingUp className="w-4 h-4" /> Get Growth Recommendations</>}
        </button>
      </div>
      {loading && !parsed && <Thinking label="Building your growth plan…" />}
      {parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-white/10 p-5 text-center">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Performance Grade</div>
              <div className={`text-5xl font-black ${gradeColor[parsed.performance_grade] || "text-white"}`}>{parsed.performance_grade}</div>
            </div>
            <div className="border border-white/10 p-5 text-center">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Visibility Score</div>
              <div className="relative w-20 mx-auto">
                <ScoreRing score={parsed.visibility_score || 0} />
                <div className="absolute inset-0 flex items-center justify-center"><span className="text-lg font-black">{parsed.visibility_score}</span></div>
              </div>
            </div>
            <div className="border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Benchmarks</div>
              {parsed.benchmarks && Object.entries(parsed.benchmarks).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs py-1 border-b border-white/5 last:border-0">
                  <span className="text-neutral-500 capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="text-neutral-300">{v}</span>
                </div>
              ))}
            </div>
          </div>
          {(parsed.top_actions || []).length > 0 && (
            <div className="border border-white/10">
              <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Top Actions to Boost Visibility</div>
              <div className="divide-y divide-white/5">
                {parsed.top_actions.map((a, i) => (
                  <div key={i} className="flex items-start gap-4 px-5 py-4">
                    <span className="font-mono text-[#D3FF24] font-black text-sm shrink-0">0{i + 1}</span>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white">{a.action}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{a.expected_impact}</div>
                    </div>
                    <span className="text-[10px] text-neutral-600 shrink-0">{a.time_required}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {(parsed.engagement_tactics || []).length > 0 && (
              <div className="border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Engagement Tactics</div>
                <ul className="space-y-1.5">{parsed.engagement_tactics.map((t, i) => <li key={i} className="text-sm text-neutral-400">· {t}</li>)}</ul>
              </div>
            )}
            {(parsed.search_tips || []).length > 0 && (
              <div className="border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Search Optimization</div>
                <ul className="space-y-1.5">{parsed.search_tips.map((t, i) => <li key={i} className="text-sm text-neutral-400">· {t}</li>)}</ul>
              </div>
            )}
          </div>
          {parsed["30_day_goal"] && <div className="border border-[#D3FF24]/20 bg-[#D3FF24]/[0.03] p-5"><div className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-2">30-Day Goal</div><p className="text-sm text-neutral-300">{parsed["30_day_goal"]}</p></div>}
          {parsed.content_frequency && <div className="border border-white/10 p-4 text-xs text-neutral-500"><span className="text-white font-semibold">Posting frequency: </span>{parsed.content_frequency}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Brand Strategy ──────────────────────────────────────────────────────────
function BrandStrategySection({ profile }) {
  const [goals, setGoals] = useState("");
  const [audience, setAudience] = useState("");
  const { loading, parsed, run } = useAIStream();

  const generate = () => {
    if (!goals.trim()) { toast.error("Describe your goals"); return; }
    const content = `Goals: ${goals}\nTarget audience: ${audience}\nHeadline: ${profile.headline || "not set"}\nIndustry: ${profile.industry || "not set"}`;
    run("/api/linkedin/optimize", { section: "brand", content, target_role: profile.target_role, industry: profile.industry });
  };

  return (
    <div>
      <SectionHeader title="Brand Strategy" subtitle="Build a cohesive personal brand that makes you magnetic to the right opportunities" />
      <div className="border border-white/10 p-5 space-y-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Your Goals</label>
          <textarea rows={3} value={goals} onChange={e => setGoals(e.target.value)}
            className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
            placeholder="Get hired at a top startup, become a recognized thought leader, transition to PM, grow my freelance business…" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Target Audience</label>
          <input value={audience} onChange={e => setAudience(e.target.value)}
            className="mt-1 w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none"
            placeholder="Tech recruiters, CTOs of Series A startups, HR managers at Fortune 500s…" />
        </div>
        <button onClick={generate} disabled={loading || !goals.trim()}
          className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</> : <><Compass className="w-4 h-4" /> Build Brand Strategy</>}
        </button>
      </div>
      {loading && !parsed && <Thinking label="Crafting your personal brand…" />}
      {parsed && (
        <div className="space-y-4">
          {parsed.brand_statement && (
            <div className="border border-[#D3FF24]/25 bg-[#D3FF24]/[0.03] p-6">
              <div className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-2">Brand Statement</div>
              <p className="text-lg font-semibold text-white leading-relaxed">"{parsed.brand_statement}"</p>
              <CopyBtn text={parsed.brand_statement} className="mt-2" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {(parsed.brand_pillars || []).length > 0 && (
              <div className="border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Brand Pillars</div>
                <ul className="space-y-2">{parsed.brand_pillars.map((p, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><span className="text-[#D3FF24] font-bold shrink-0">{i + 1}.</span>{p}</li>)}</ul>
              </div>
            )}
            {(parsed.content_themes || []).length > 0 && (
              <div className="border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Content Themes</div>
                <ul className="space-y-2">{parsed.content_themes.map((t, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><ArrowRight className="w-3.5 h-3.5 text-[#0055FF] mt-0.5 shrink-0" />{t}</li>)}</ul>
              </div>
            )}
            {parsed.tone_of_voice && (
              <div className="border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Tone of Voice</div>
                <p className="text-sm text-neutral-300">{parsed.tone_of_voice}</p>
              </div>
            )}
            {parsed.differentiation && (
              <div className="border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Differentiation</div>
                <p className="text-sm text-neutral-300">{parsed.differentiation}</p>
              </div>
            )}
          </div>
          {(parsed["30_day_plan"] || []).length > 0 && (
            <div className="border border-white/10">
              <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest font-semibold text-neutral-400">30-Day Action Plan</div>
              <div className="divide-y divide-white/5">
                {parsed["30_day_plan"].map((step, i) => (
                  <div key={i} className="flex items-start gap-4 px-5 py-3">
                    <span className="font-mono text-[#D3FF24] font-black text-sm shrink-0 w-6">W{Math.floor(i / 2) + 1}</span>
                    <p className="text-sm text-neutral-300">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {parsed.headline_brand_cue && <div className="border border-white/10 p-4 text-sm"><span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mr-2">Headline cue: </span><span className="text-neutral-300">{parsed.headline_brand_cue}</span></div>}
        </div>
      )}
    </div>
  );
}

// ─── Settings ────────────────────────────────────────────────────────────────
function SettingsSection() {
  const [checked, setChecked] = useState({});
  const toggle = id => setChecked(c => ({ ...c, [id]: !c[id] }));

  const ITEMS = [
    { id: "url",         cat: "Profile URL",   title: "Customize your LinkedIn URL",         impact: "High",   desc: "Profile → Edit → Contact Info → Edit URL. Use: linkedin.com/in/firstname-lastname. Remove random numbers — recruiters notice." },
    { id: "opentowork", cat: "Job Search",     title: "Configure Open to Work",              impact: "High",   desc: "Settings → Job seeking → Open to Work. Set to 'Recruiters only' to stay hidden from your current employer. Add all job types." },
    { id: "creator",    cat: "Visibility",     title: "Enable Creator Mode",                 impact: "Medium", desc: "Profile → Resources → Creator Mode. Converts 'Connect' to 'Follow', unlocks newsletter, adds 5 topic hashtags to your profile." },
    { id: "photo",      cat: "First Impression", title: "Professional headshot",             impact: "High",   desc: "Profiles with photos get 21× more views. Face should fill 60% of the frame. Use a plain or blurred background. No group shots." },
    { id: "banner",     cat: "First Impression", title: "Update your banner image",          impact: "Medium", desc: "1584 × 396px. Use Canva to create a branded banner with your role, tagline, or website. Default blue = missed opportunity." },
    { id: "contact",    cat: "Accessibility",  title: "Add contact info & website",          impact: "Medium", desc: "Settings → Visibility → Contact info. Add email, phone, personal website, or portfolio. Reduces friction for recruiters to reach you." },
    { id: "seo",        cat: "Search SEO",     title: "Add keywords in 'Other names'",       impact: "Medium", desc: "Profile → Edit → Other names. Add your maiden name, nickname, or alternate spellings so more searches find you." },
    { id: "featured",   cat: "Social Proof",   title: "Pin your best content to Featured",   impact: "Medium", desc: "First thing visitors see below your headline. Pin 1–3 items: a project, article, portfolio, or recommendation screenshot." },
    { id: "skills",     cat: "Search SEO",     title: "Max out to 50 skills",                impact: "High",   desc: "LinkedIn lets you add 50 skills. Most people add 10. Skills directly affect search ranking. Prioritize industry tools and methodologies." },
    { id: "endorsements", cat: "Social Proof", title: "Get 10+ endorsements on top skills", impact: "Medium", desc: "Skills with 10+ endorsements rank higher in recruiter search. Ask peers — LinkedIn's Smart Suggestions makes it one-click for them." },
    { id: "activity",   cat: "Visibility",     title: "Post or comment at least 2× per week",impact: "High",   desc: "Activity = visibility. Commenting on 3 posts in your niche takes 5 minutes and surfaces your profile to new audiences every time." },
    { id: "newsletter", cat: "Authority",      title: "Start a LinkedIn newsletter",         impact: "Low",    desc: "Creator Mode → Write Articles → Start a Newsletter. Subscribers are notified every issue — compound audience building over time." },
  ];

  const cats = [...new Set(ITEMS.map(i => i.cat))];
  const done = Object.values(checked).filter(Boolean).length;
  const ic = { High: "text-[#D3FF24] border-[#D3FF24]/30", Medium: "text-amber-400 border-amber-400/30", Low: "text-neutral-400 border-neutral-400/30" };

  return (
    <div>
      <SectionHeader title="Settings & Optimization Checklist" subtitle="12 LinkedIn settings most people miss — each one directly boosts visibility or recruiter response rate" />
      <div className="border border-white/10 p-4 mb-6 flex items-center gap-4">
        <div className="relative shrink-0">
          <ScoreRing score={Math.round((done / ITEMS.length) * 100)} />
          <div className="absolute inset-0 flex items-center justify-center"><span className="text-lg font-black">{done}</span></div>
        </div>
        <div>
          <div className="text-sm font-semibold">{done} of {ITEMS.length} completed</div>
          <div className="text-xs text-neutral-500 mt-0.5">Complete all 12 to maximize LinkedIn visibility</div>
        </div>
        <div className="ml-auto">
          <div className="w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#D3FF24] rounded-full transition-all duration-500" style={{ width: `${(done / ITEMS.length) * 100}%` }} />
          </div>
        </div>
      </div>
      {cats.map(cat => (
        <div key={cat} className="mb-4">
          <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-700 font-bold px-1 mb-2">{cat}</div>
          <div className="border border-white/10 divide-y divide-white/5">
            {ITEMS.filter(i => i.cat === cat).map(item => (
              <div key={item.id} className={`flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition ${checked[item.id] ? "opacity-50" : ""}`}
                onClick={() => toggle(item.id)}>
                <div className={`w-5 h-5 border flex items-center justify-center shrink-0 mt-0.5 transition ${checked[item.id] ? "border-[#D3FF24] bg-[#D3FF24]" : "border-white/20"}`}>
                  {checked[item.id] && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-semibold ${checked[item.id] ? "line-through text-neutral-600" : "text-white"}`}>{item.title}</span>
                    <span className={`text-[9px] uppercase tracking-wider font-bold border px-1.5 py-0.5 ${ic[item.impact]}`}>{item.impact}</span>
                  </div>
                  <p className="text-xs text-neutral-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Coming Soon placeholder ─────────────────────────────────────────────────
function ComingSoon({ title, subtitle, icon: Icon }) {
  return (
    <div>
      <SectionHeader title={title} subtitle={subtitle} />
      <div className="border border-white/10 p-20 text-center">
        <Icon className="w-12 h-12 text-neutral-800 mx-auto mb-4" strokeWidth={1.25} />
        <p className="text-neutral-600 font-semibold text-sm">Coming Soon</p>
        <p className="text-neutral-700 text-xs mt-1">This feature is under development</p>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LinkedInOptimizer() {
  const [section, setSection] = useState("overview");
  const [profile, setProfile] = useState({
    name: "", headline: "", about: "", location: "",
    industry: "", target_role: "", skills: [], experience: [], education: [],
  });

  useEffect(() => {
    fetch("/api/linkedin/profile", { headers: { Authorization: `Bearer ${tkn()}` } })
      .then(r => r.json())
      .then(d => { if (d && Object.keys(d).length) setProfile(p => ({ ...p, ...d })); })
      .catch(() => {});
  }, []);

  const renderSection = () => {
    switch (section) {
      case "overview":        return <OverviewSection profile={profile} setProfile={setProfile} setSection={setSection} />;
      case "analysis":        return <AnalysisSection />;
      case "headline":        return <HeadlineSection profile={profile} />;
      case "about":           return <AboutSection profile={profile} />;
      case "experience":      return <ExperienceSection profile={profile} />;
      case "skills":          return <SkillsSection profile={profile} setProfile={setProfile} />;
      case "ats":             return <ATSSection profile={profile} />;
      case "jobmatch":        return <JobMatchSection profile={profile} />;
      case "content":         return <ContentStudioSection profile={profile} />;
      case "education":       return <EducationSection profile={profile} />;
      case "projects":        return <ProjectsSection profile={profile} />;
      case "certifications":  return <CertificationsSection profile={profile} />;
      case "recommendations": return <RecommendationsSection profile={profile} />;
      case "featured":        return <FeaturedSection profile={profile} />;
      case "resume":          return <ResumeSyncSection />;
      case "networking":      return <NetworkingSection profile={profile} />;
      case "recruiter":       return <RecruiterModeSection profile={profile} />;
      case "analytics":       return <AnalyticsSection profile={profile} />;
      case "brand":           return <BrandStrategySection profile={profile} />;
      case "settings":        return <SettingsSection />;
      default:                return <OverviewSection profile={profile} setProfile={setProfile} setSection={setSection} />;
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Sub-nav sidebar */}
      <aside className="w-52 shrink-0 border-r border-white/10 py-5 sticky top-0 h-screen overflow-y-auto">
        <div className="px-4 mb-5 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#0A66C2] flex items-center justify-center shrink-0">
              <LinkedinIcon className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest leading-none">LinkedIn<br /><span className="text-[#D3FF24]">Optimizer</span></span>
          </div>
        </div>
        {NAV.map(({ group, items }) => (
          <div key={group} className="mb-2">
            <div className="px-4 py-1 text-[9px] uppercase tracking-widest text-neutral-700 font-bold">{group}</div>
            {items.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setSection(id)}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-xs transition border-l-2 text-left ${
                  section === id
                    ? "border-[#D3FF24] bg-white/[0.03] text-white"
                    : "border-transparent text-neutral-500 hover:text-neutral-300 hover:bg-white/[0.02]"
                }`}>
                <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
                {label}
              </button>
            ))}
          </div>
        ))}
      </aside>

      {/* Content */}
      <main className="flex-1 px-10 py-10 max-w-4xl overflow-y-auto">
        {renderSection()}
      </main>
    </div>
  );
}
